import React, { useEffect, useState } from 'react';
import { Shield, ShieldCheck, ShieldAlert, Key, Calendar, Users, Package, Loader2, CheckCircle, XCircle } from 'lucide-react';
import licenseService, { LicenseStatus } from '../services/licenseService';

const License: React.FC = () => {
  const [status, setStatus] = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState('');
  const [activating, setActivating] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const refresh = async () => {
    setLoading(true);
    setFeedback(null);
    const s = await licenseService.getStatus();
    setStatus(s);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const handleActivate = async () => {
    if (!key.trim()) {
      setFeedback({ ok: false, msg: 'Lisans anahtari bos olamaz' });
      return;
    }
    setActivating(true);
    setFeedback(null);
    const res = await licenseService.activate(key.trim());
    setActivating(false);
    if (res.success) {
      setFeedback({ ok: true, msg: res.message || 'Lisans aktif edildi. Sayfa yenileniyor...' });
      setKey('');
      setTimeout(() => {
        window.location.reload();
      }, 1200);
    } else {
      setFeedback({ ok: false, msg: res.message || 'Aktivasyon basarisiz' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const isActive = status?.active === true;
  const isExpired = status?.expired === true;
  const isExpiringSoon = status?.expiringSoon === true;

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
          isActive && !isExpired ? 'bg-emerald-100 text-emerald-600'
          : isExpired ? 'bg-red-100 text-red-600'
          : 'bg-amber-100 text-amber-600'
        }`}>
          {isActive && !isExpired ? <ShieldCheck size={32} />
           : isExpired ? <XCircle size={32} />
           : <ShieldAlert size={32} />}
        </div>
        <div>
          <h1 className="text-2xl font-black text-slate-800">Lisans Yonetimi</h1>
          <p className="text-slate-500 text-sm">
            {isActive && !isExpired ? 'Lisansiniz aktif ve gecerli'
              : isExpired ? 'Lisansiniz suresi doldu'
              : 'Lisans henuz aktif edilmemis'}
          </p>
        </div>
      </div>

      {/* Aktif lisans bilgileri */}
      {isActive && (
        <div className={`rounded-2xl border p-6 ${
          isExpired ? 'bg-red-50 border-red-200'
          : isExpiringSoon ? 'bg-amber-50 border-amber-200'
          : 'bg-emerald-50 border-emerald-200'
        }`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Shield size={20} className="text-slate-500 mt-0.5" />
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Musteri</p>
                <p className="text-sm font-bold text-slate-800">{status?.customerName || '-'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Package size={20} className="text-slate-500 mt-0.5" />
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Plan</p>
                <p className="text-sm font-bold text-slate-800 uppercase">{status?.plan || '-'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar size={20} className="text-slate-500 mt-0.5" />
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Bitis Tarihi</p>
                <p className="text-sm font-bold text-slate-800">
                  {status?.expiryDate || '-'}
                  {typeof status?.daysRemaining === 'number' && (
                    <span className={`ml-2 text-xs font-normal ${
                      status.daysRemaining < 0 ? 'text-red-600'
                      : status.daysRemaining < 30 ? 'text-amber-600'
                      : 'text-slate-500'
                    }`}>
                      ({status.daysRemaining < 0 ? `${Math.abs(status.daysRemaining)} gun once doldu`
                        : `${status.daysRemaining} gun kaldi`})
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users size={20} className="text-slate-500 mt-0.5" />
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Maksimum Personel</p>
                <p className="text-sm font-bold text-slate-800">{status?.maxEmployees ?? '-'}</p>
              </div>
            </div>
          </div>

          {status?.modules && status.modules.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Aktif Moduller</p>
              <div className="flex flex-wrap gap-2">
                {status.modules.map(m => (
                  <span key={m} className="px-2.5 py-1 bg-white border border-slate-200 rounded-full text-xs font-semibold text-slate-700">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Inaktif / expired / activation formu */}
      {(!isActive || isExpired || isExpiringSoon) && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Key size={18} className="text-blue-600" />
            <h2 className="text-lg font-black text-slate-800">
              {isExpired ? 'Lisansi Yenile' : isActive ? 'Yeni Lisans Anahtari Gir' : 'Lisans Aktifle'}
            </h2>
          </div>

          <p className="text-sm text-slate-500 mb-4">
            {status?.message || 'Size verilen lisans anahtarini asagiya yapistirin ve Aktifle butonuna basin.'}
          </p>

          <textarea
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="MAXPDKS-... (Uzun karakter dizisi — kopyala-yapistir)"
            rows={4}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-xs font-mono resize-y"
          />

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleActivate}
              disabled={activating || !key.trim()}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
            >
              {activating ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              {activating ? 'Aktif Ediliyor...' : 'Aktifle'}
            </button>
            <button
              onClick={refresh}
              className="px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all"
            >
              Yenile
            </button>
          </div>

          {feedback && (
            <div className={`mt-4 px-4 py-3 rounded-xl text-sm flex items-center gap-2 ${
              feedback.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {feedback.ok ? <CheckCircle size={16} /> : <XCircle size={16} />}
              {feedback.msg}
            </div>
          )}
        </div>
      )}

      {/* Yardim */}
      <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500">
        <p className="font-bold text-slate-700 mb-1">Yardim</p>
        <p>Lisans anahtarini kaybettiyseniz veya yenilemek istiyorsaniz: <span className="font-semibold text-slate-700">maxbilgisayar@gmail.com</span></p>
      </div>
    </div>
  );
};

export default License;
